import React from 'react';
import styled from 'styled-components';
import moment from 'moment';
import 'moment/dist/locale/ro';
import 'moment/dist/locale/pt';
import 'moment/dist/locale/es';

import { useNavigate, useMatches, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppSelector, useAppDispatch } from '@/store';
import { saveCalendarSelection } from '@/modules/bets/store/actions/calendar';
import { prematchFetchSportByDay } from '@/modules/bets/store/actions/prematch';
import { appToggleCompetitions } from '@/modules/bets/store/actions/app';
import { sportNameToId, rerouteUrl } from '../../utils/functions';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

import './index.scss';

type PrematchCalendar = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
    ukLayout: boolean;
  };
};

const defaultProps = {
  className: '',
  styleText: '',
  properties: {
    dsType: '',
  },
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const Calendar = (componentProps: PrematchCalendar) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();

  //console.log('Calendar[componentProps]', props);

  const showCompetitions = useAppSelector<any>((state) => state.bets.app.showCompetitions);
  const filterByDate = useAppSelector<any>((state: any) => state.bets.calendar.filterBy);
  const range = useAppSelector<any>((state: any) => state.bets.calendar.range);
  let selectedSport = useAppSelector<any>((state) => state.bets.navSports.prematch);
  const prematchSports = useAppSelector<any>((state) => state.bets.prematch.sportsMeta);
  const liveSports = useAppSelector<any>((state) => state.bets.live.sports);
  const ukLayout = props?.properties?.ukLayout ?? false;

  const navigate = useNavigate();
  const matches = useMatches();
  const [searchParams] = useSearchParams();

  if (matches?.length > 0) {
    const match = matches[matches?.length - 1];
    if (match?.params?.idSport) {
      selectedSport = match?.params?.idSport;
      if (selectedSport) selectedSport = sportNameToId(selectedSport);
    }
  }

  React.useEffect(() => {
    if (searchParams?.get('sd') === '0') {
      dispatch(saveCalendarSelection(0, ''));
      dispatch(prematchFetchSportByDay({ day: 0, idSport: selectedSport, noDays: 1 }));
    }
    return () => {
      // @ts-ignore
      dispatch(appToggleCompetitions(false));
    };
  }, [dispatch, selectedSport, searchParams]);

  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    let live = false;
    if (e.currentTarget.dataset.live === 'true') {
      live = true;
    } else {
      //e.stopPropagation();
    }

    let date = null;
    if (e.currentTarget.dataset.date) {
      date = e.currentTarget.dataset.date;
    }

    if (date != null) {
      dispatch(
        saveCalendarSelection(date === '0' ? 0 : date, date === '0' ? '' : e.currentTarget.dataset.range ?? '24h'),
      );
    }

    if (!live) {
      let noDays = 1;

      if (e.currentTarget.dataset.range === '72h') {
        noDays = 3;
      }

      dispatch(prematchFetchSportByDay({ day: date === '0' ? 0 : date, idSport: selectedSport, noDays: noDays }));
      if (window.location.href.indexOf('pre-match') === -1) {
        if (window.location.href.indexOf('/winnerfun/') > -1) {
          navigate(`/winnerfun/pre-match/${selectedSport}`);
        } else {
          navigate(rerouteUrl(`/${window.config.sportBetsUrlRoot ?? 'bets'}/pre-match/${selectedSport}`));
        }
      }
    }
  };

  const onToggleCompetitions = () => {
    dispatch(appToggleCompetitions());
  };

  const days: any = [];
  for (let i = 0; i < 7; i++) {
    const ts = moment().add(i, 'days').startOf('day').valueOf() + '';
    days.push(ts);
  }

  let items: any = [];

  if (!ukLayout) {
    let firstWeekDay = false;
    Array.isArray(days) &&
      days.forEach((d) => {
        // eslint-disable-next-line
        if (d && filterByDate != null && d.toString() === filterByDate.toString()) firstWeekDay = true;
      });

    if (filterByDate.toString() === '0') firstWeekDay = true;

    if (Array.isArray(days) && firstWeekDay) {
      items = days.map((date) => {
        //console.log("prematch date", date);
        const di = parseInt(date, 10);

        let lang = i18n.language;
        if (lang === 'mx') lang = 'es';
        const d = moment(di).locale(lang);

        let title = d.format('ddd D MMM');
        const diff = moment().startOf('day').diff(d, 'days');
        if (diff > -2) {
          if (diff === 0) title = 'Today';
          if (diff === -1) title = 'Tomorrow';
        }

        return {
          active:
            window.location.href.indexOf('live') === -1 &&
            window.location.href.indexOf('outrights') === -1 &&
            date &&
            filterByDate != null &&
            date.toString() === filterByDate.toString(),
          all: false,
          subtitle: d.format('dddd'),
          title: title,
          formatted: d.format('YYYY-MM-DD'),
          date,
          hot: false,
          onClick,
        };
      });
    } else {
      let genDays = days > 0 ? days : 1;

      let offsetDays = 0;
      if (!firstWeekDay) {
        const now = moment().startOf('day');
        const selectedDay = moment(filterByDate).startOf('day');
        const diffDays = selectedDay.diff(now, 'days');

        if (diffDays > 7) {
          genDays = 7;
          offsetDays = Math.floor(diffDays / 7) * 7;
        }
      }

      let lang = i18n.language;
      if (lang === 'mx') lang = 'es';

      let d;

        if (offsetDays) {
        d = moment().add(offsetDays, 'd').locale(lang);
        } else {
        d = moment().locale(lang);
      }

      for (let i = 0; i < genDays; i++) {
        let title = d.format('ddd D MMM');
        const diff = moment().startOf('day').diff(d, 'days');
        if (diff > -2) {
          if (diff === 0) title = 'Today';
          if (diff === -1) title = 'Tomorrow';
        }

        items.push({
          active:
            window.location.href.indexOf('live') === -1 &&
            window.location.href.indexOf('outrights') === -1 &&
            filterByDate != null &&
            d.valueOf().toString() === filterByDate.toString(),
          all: false,
          subtitle: d.format('dddd'),
          title: title,
          formatted: d.format('YYYY-MM-DD'),
          date: d.startOf('day').valueOf().toString(),
          hot: false,
          onClick,
        });
        d = d.add(1, 'd');
      }
    }

    if (window.config.euroLeagueEnabled === '1') {
      items.unshift({
        euro: true,
      });
    }

    if (window.config.championshipEnabled === '1') {
      items.unshift({
        championship: true,
      });
    }
    if (window.config.outRightsEnabled === '1') {
      items.unshift({
        outRights: true,
      });
    }

    items.unshift({
      live: true,
      title: 'Live',
      date: items[0].date,
      onClick,
    });

    items.unshift({
      active: showCompetitions,
      filter: true,
      date: items[0].date,
      onClick: onToggleCompetitions,
    });
  } else {
    const ts = moment().startOf('day').valueOf() + '';

    items.push({
      active:
        window.location.href.indexOf('live') === -1 &&
        window.location.href.indexOf('outrights') === -1 &&
        filterByDate != null &&
        ts === filterByDate.toString() &&
        range === '3h',
      all: false,
      subtitle: '',
      title: '3h',
      formatted: '',
      date: ts,
      hot: false,
      range: '3h',
      onClick,
    });

    items.push({
      active:
        window.location.href.indexOf('live') === -1 &&
        window.location.href.indexOf('outrights') === -1 &&
        filterByDate != null &&
        ts === filterByDate.toString() &&
        range === '24h',
      all: false,
      subtitle: '',
      title: '24h',
      formatted: '',
      date: ts,
      hot: false,
      range: '24h',
      onClick,
    });

    items.push({
      active:
        window.location.href.indexOf('live') === -1 &&
        window.location.href.indexOf('outrights') === -1 &&
        filterByDate != null &&
        ts === filterByDate.toString() &&
        range === '72h',
      all: false,
      subtitle: '',
      title: '72h',
      formatted: '',
      date: ts,
      hot: false,
      range: '72h',
      onClick,
    });

    items.unshift({
      live: true,
      title: 'Live',
      date: ts,
      onClick,
    });
  }

  items.push({
    active: filterByDate != null && '0' === filterByDate.toString(),
    subtitle: '',
    all: true,
    title: t('All days'),
    formatted: '',
    date: 0,
    onClick,
  });

  const contextValue = {
    days: items,
    active: items.find((i: any) => i.active)?.title,
    selectedSport: selectedSport,
    hasPrematchSport: prematchSports?.[selectedSport]?.numberOfEvents > 0,
    hasLiveSport: liveSports?.[selectedSport] != null,
  };

  //console.log('Calendar[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={componentProps.className ?? ''} $styleText={componentProps.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Calendar;
